import React, { useState, useEffect, useCallback } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useViewState } from "../model/ViewState";
import { useProjectStore } from "../model/ProjectStore";
import { useSnackbar } from "notistack"; 

const getApiEndpoint = (path) => {
  let api = path;
  if (process.env.NODE_ENV !== "production") {
    api = "http://localhost:7071" + api;
  }
  return api;
};

function DialogCreateNewProject({ open, onCancel }) {
  const { enqueueSnackbar } = useSnackbar();
  const view = useViewState();
  const store = useProjectStore();
  
  const [name, setName] = useState('');
  const [isCloning, setIsCloning] = useState(false);
  const [projectToClone, setProjectToClone] = useState(null); 
  const [selectedProjectKey, setSelectedProjectKey] = useState(''); 

  const [existingProjects, setExistingProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);

  const handleCreateNewProject = useCallback(async (name, isCloning, projectToClone) => {
    view.setNewProjectDialogState(false);

    let newProjectData;
    if (isCloning && projectToClone) {

      setLoadingProjects(true);
      const encodedName = encodeURIComponent(name);
      let cloneAPI = `/api/project/clone/${projectToClone.id}/${encodedName}`;
      if (process.env.NODE_ENV !== "production") {
        cloneAPI = "http://localhost:7071" + cloneAPI;
      }

      try {
        const response = await fetch(cloneAPI, {
          method: "POST",
          headers: {
            Accept: "application/json, text/plain, */*",
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          let errorText = await response.text();
          try {
            const errorJson = JSON.parse(errorText);
            errorText = errorJson.Error || errorText;
          } catch (e) { /* ignore if not JSON */ }

          throw new Error(`Clone failed (${response.status} ${response.statusText}): ${errorText}`);
        }

        const result = await response.json();
        const newProjectId = result.NewProjectId; 

        enqueueSnackbar(`Project successfully cloned as "${name}"! Loading new project...`, { 
          variant: "success" 
        });

        loadFromCloud(newProjectId);

      } catch (error) {
        enqueueSnackbar(`Cloning Error: ${error.message}`, { variant: "error" });
      } finally {
        setLoadingProjects(false);
      }
    } else {
      alert("not implemented");
    }
  }, []);

  const loadFromCloud = (projectId) => {
    // setIsRefreshing(true);
    view.setProjectIsLoading(true);
    let getAPI =
      "/api/project/" + projectId;
    if (process.env.NODE_ENV !== "production") {
      getAPI = "http://localhost:7071" + getAPI;
    }
    fetch(getAPI, {
      method: "GET",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
      },
      redirect: "manual",
    })
      .then((response) => {
        if (response.type === "opaqueredirect") {
          enqueueSnackbar(
            "Failed to fetch. Hence the page will be automatically refreshed, and signin again.",
            { variant: "error" }
          );
          setTimeout(() => {
            window.location.href = "/login";
          }, 1000);
          return;
        }
        if (!response.ok) {
          response
            .text()
            .then((text) => {
              throw Error(response.statusText + ": " + text);
            })
            .catch((error) =>
              enqueueSnackbar(error.message, { variant: "error" })
            );
        } else {
          response.json().then((result) => {
            store.dispose();
            store.updateStore(result);
            // contentKey = contentKey + 1;
            // setIsRefreshing(false);
            view.setProjectIsLoading(false);
            // setRefreshSuccess(true);
            enqueueSnackbar("The latest project data has been loaded!", {
              variant: "success",
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "center",
              },
            })
          });
        }
      })
      .catch((error) =>
        enqueueSnackbar(error.message + { variant: "error" })
      );
  };
  const fetchExistingProjects = React.useCallback(() => {
    setLoadingProjects(true);
    const getAPI = getApiEndpoint("/api/projects/list");

    fetch(getAPI, {
      method: "GET",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
      },
      redirect: "manual",
    })
      .then((response) => {
        if (response.type === "opaqueredirect") {
          enqueueSnackbar(
            "Failed to fetch. Please try again.",
            { variant: "error" }
          );
          return;
        }
        if (!response.ok) {
          response.text().then((text) => {
            throw Error(response.statusText + ": " + text);
          }).catch((error) => {
            enqueueSnackbar(error.message, { variant: "error" });
            setLoadingProjects(false);
          });
        } else {
          response.json().then((result) => {
            setExistingProjects(result);
            setLoadingProjects(false);
          });
        }
      })
      .catch((error) => {
        enqueueSnackbar(error.message, { variant: "error" });
        setLoadingProjects(false);
      });
  }, [enqueueSnackbar]);
  
  useEffect(() => {
    if (open && isCloning && existingProjects.length === 0 && !loadingProjects) {
      fetchExistingProjects();
    }
  }, [open]);

  function handleNameChange(event) {
    setName(event.target.value);
  }

  function handleCloneChange(event) {
    const checked = event.target.checked;
    setIsCloning(checked);
    if (checked && existingProjects.length === 0 && !loadingProjects) {
      fetchExistingProjects();
    }
    if (!checked) {
      setProjectToClone(null);
      setSelectedProjectKey('');
    }
  }

  function handleProjectSelectChange(event) {
    const selectedKey = event.target.value;
    setSelectedProjectKey(selectedKey);

    const selectedProject = existingProjects.find(p => p.id === selectedKey);
    setProjectToClone(selectedProject || null);
  }

  function handleSaveClick() {
    //onSave(name, isCloning, projectToClone); 
    
    setName('');
    setIsCloning(false);
    setProjectToClone(null);
    setSelectedProjectKey('');
  }
  
  const isSaveDisabled = !name.trim() || (isCloning && !projectToClone);

  return (
    <Dialog open={open} onClose={onCancel} aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">New Project</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Please enter a name for your new project:
        </DialogContentText>
        <TextField
          autoFocus
          margin="dense"
          id="name"
          label="Project Name"
          type="text"
          value={name}
          onChange={handleNameChange}
          fullWidth
        />
        
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox 
                checked={isCloning} 
                onChange={handleCloneChange} 
                name="clone-project-content" 
                color="primary"
              />
            }
            label="Clone from an existing project"
          />

          {isCloning && (
            <FormControl fullWidth margin="dense" disabled={loadingProjects}>
              <InputLabel id="clone-project-label">Clone From Project</InputLabel>
              <Select
                labelId="clone-project-label"
                id="clone-project-select"
                value={selectedProjectKey}
                label="Clone From Project"
                onChange={handleProjectSelectChange}
              >
                {loadingProjects && (
                  <MenuItem value="" disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} /> Loading projects...
                  </MenuItem>
                )}

                {!loadingProjects && (
                  <MenuItem value="">
                    <em>Select a project</em>
                  </MenuItem>
                )}

                {!loadingProjects && existingProjects.length === 0 && (
                  <MenuItem value="" disabled>
                    No existing projects found.
                  </MenuItem>
                )}

                {!loadingProjects && existingProjects.length > 0 && (
                  existingProjects.map((project) => (
                    <MenuItem 
                      key={project.id} 
                      value={project.id}
                    >
                      {project.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} color="secondary">
          Cancel
        </Button>
        <Button 
          onClick={() => handleCreateNewProject(name, isCloning, projectToClone)} 
          color="primary" 
          disabled={isSaveDisabled}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DialogCreateNewProject;